/**
 * navir-gallery-filter.js
 * Filtre les médias de la galerie produit par couleur.
 * S'active uniquement sur les fiches produit (product-info présent dans le DOM).
 */
(function () {
  'use strict';

  var GENERIC_ALT = 'toutes les couleurs';
  var COLOR_NAMES = ['couleur', 'nuance', 'color', 'colour'];
  var MOBILE_BREAKPOINT = 1024;

  // ── Utilitaires ────────────────────────────────────────────────────────────

  function normalize(str) {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim()
      .replace(/[-_\s]+/g, ' ');
  }

  function sortByVideoFirst(slides) {
    return slides.slice().sort(function (a, b) {
      var aIsVideo = a.dataset.type === 'video' || a.dataset.type === 'external_video';
      var bIsVideo = b.dataset.type === 'video' || b.dataset.type === 'external_video';
      if (aIsVideo && !bIsVideo) return -1;
      if (!aIsVideo && bIsVideo) return 1;
      return parseInt(a.dataset.navirIndex, 10) - parseInt(b.dataset.navirIndex, 10);
    });
  }

  function triggerVideoAutoplay(slide) {
    var deferred = slide.querySelector('[data-deferred-media]');
    if (!deferred) return;
    if (deferred.getAttribute('loaded') !== 'true') {
      var btn = slide.querySelector('[data-deferred-media-button]');
      if (btn) btn.dispatchEvent(new Event('click'));
    } else {
      // Vidéo déjà chargée — déclenche la lecture via l'événement du thème
      slide.dispatchEvent(new CustomEvent('theme:media:visible', { bubbles: true }));
    }
  }

  function triggerVideoPause(slide) {
    slide.dispatchEvent(new CustomEvent('theme:media:hidden', { bubbles: true }));
  }

  // ── Détection couleur ──────────────────────────────────────────────────────

  function findColorOptionIndex(productInfo) {
    var inputs = productInfo.querySelectorAll('input[data-single-option-selector]');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var name = (input.getAttribute('name') || '').toLowerCase();
      var match = name.match(/options\[(.+)\]/);
      if (match) {
        var optName = normalize(match[1]);
        if (COLOR_NAMES.some(function (cn) { return optName.indexOf(cn) !== -1; })) {
          var raw = input.getAttribute('data-index') || '';
          var num = parseInt(raw.replace(/\D/g, ''), 10);
          return isNaN(num) ? -1 : num - 1;
        }
      }
    }
    var swatchInput = productInfo.querySelector('.swatch__input[data-index]');
    if (swatchInput) {
      var raw = swatchInput.getAttribute('data-index') || '';
      var num = parseInt(raw.replace(/\D/g, ''), 10);
      return isNaN(num) ? -1 : num - 1;
    }
    return -1;
  }

  function getSelectedColor(productInfo, colorOptionIndex) {
    var variantEl = productInfo.querySelector('variant-selects [data-selected-variant]');
    if (variantEl) {
      try {
        var variant = JSON.parse(variantEl.innerHTML);
        if (variant && variant.options && variant.options[colorOptionIndex] !== undefined) {
          return variant.options[colorOptionIndex];
        }
      } catch (e) {}
    }
    var dataIndex = colorOptionIndex + 1;
    var checkedInput = productInfo.querySelector(
      'input[data-single-option-selector][data-index="' + dataIndex + '"]:checked,' +
      'input[data-single-option-selector][data-index="option' + dataIndex + '"]:checked'
    );
    return checkedInput ? checkedInput.value : null;
  }

  // ── Pool Flickity ──────────────────────────────────────────────────────────

  var pool = null;

  function getPool() {
    if (!pool) {
      pool = document.createElement('div');
      pool.id = 'navir-gallery-pool';
      pool.setAttribute('aria-hidden', 'true');
      pool.style.cssText = 'display:none;position:absolute;width:0;height:0;overflow:hidden;';
      document.body.appendChild(pool);
    }
    return pool;
  }

  function restoreFromPool(productInfo) {
    if (!pool) return;
    var slider = productInfo.querySelector('[data-product-single-media-slider]');
    var thumbContainer = productInfo.querySelector('[data-product-single-media-thumbs]');
    Array.from(pool.querySelectorAll('[data-navir-slide]')).forEach(function (slide) {
      if (slider) {
        slide.setAttribute('data-product-single-media-wrapper', '');
        slide.classList.remove('navir-media--hidden');
        slider.appendChild(slide);
      }
    });
    if (thumbContainer) {
      Array.from(pool.querySelectorAll('[data-navir-thumb]')).forEach(function (thumb) {
        thumbContainer.appendChild(thumb);
      });
    }
  }

  // ── Filtre desktop (CSS class toggle) ─────────────────────────────────────

  function applyDesktopFilter(productInfo, colorValue) {
    restoreFromPool(productInfo);
    var normalizedColor = colorValue ? normalize(colorValue) : null;
    var gallery = productInfo.querySelector('[data-product-single-media-group]');
    if (!gallery) return;

    var slides = gallery.querySelectorAll('[data-product-single-media-wrapper][data-media-alt]');
    slides.forEach(function (slide) {
      var altN = normalize(slide.dataset.mediaAlt);
      var isVideo = slide.dataset.type === 'video' || slide.dataset.type === 'external_video';
      var shouldShow = altN === GENERIC_ALT || isVideo || (normalizedColor && altN === normalizedColor);
      var wasHidden = slide.classList.contains('navir-media--hidden');
      slide.classList.toggle('navir-media--hidden', !shouldShow);
      if (shouldShow && wasHidden) triggerVideoAutoplay(slide);
      if (!shouldShow && !wasHidden) triggerVideoPause(slide);
    });

    var thumbContainer = productInfo.querySelector('[data-product-single-media-thumbs]');
    if (thumbContainer) {
      var thumbs = thumbContainer.querySelectorAll('.product-single__thumbnail[data-media-alt]');
      thumbs.forEach(function (thumb) {
        var altN = normalize(thumb.dataset.mediaAlt);
        var isVideo = thumb.dataset.type === 'video' || thumb.dataset.type === 'external_video';
        var shouldShow = altN === GENERIC_ALT || isVideo || (normalizedColor && altN === normalizedColor);
        thumb.classList.toggle('navir-thumb--hidden', !shouldShow);
      });
    }
  }

  // ── Filtre Flickity (déplacement DOM) ─────────────────────────────────────

  function applyFlickityFilter(productInfo, colorValue) {
    var normalizedColor = colorValue ? normalize(colorValue) : null;
    var slider = productInfo.querySelector('[data-product-single-media-slider]');
    var thumbContainer = productInfo.querySelector('[data-product-single-media-thumbs]');
    if (!slider) return;

    var poolEl = getPool();

    var inSlider = Array.from(slider.querySelectorAll('[data-navir-slide]'));
    var inPool = Array.from(poolEl.querySelectorAll('[data-navir-slide]'));
    var allSlides = inSlider.concat(inPool).sort(function (a, b) {
      return parseInt(a.dataset.navirIndex, 10) - parseInt(b.dataset.navirIndex, 10);
    });

    var allThumbs = [];
    if (thumbContainer) {
      var inThumbContainer = Array.from(thumbContainer.querySelectorAll('[data-navir-thumb]'));
      var inThumbPool = Array.from(poolEl.querySelectorAll('[data-navir-thumb]'));
      allThumbs = inThumbContainer.concat(inThumbPool).sort(function (a, b) {
        return parseInt(a.dataset.navirIndex, 10) - parseInt(b.dataset.navirIndex, 10);
      });
    }

    var toShow = allSlides.filter(function (slide) {
      var altN = normalize(slide.dataset.mediaAlt || '');
      var isVideo = slide.dataset.type === 'video' || slide.dataset.type === 'external_video';
      return altN === GENERIC_ALT || isVideo || (normalizedColor && altN === normalizedColor);
    });
    var toHideSet = new Set(
      allSlides
        .filter(function (s) { return toShow.indexOf(s) === -1; })
        .map(function (s) { return s.dataset.id; })
    );

    if (typeof productInfo.destroyProductSlider === 'function') {
      productInfo.destroyProductSlider();
    }

    allSlides.forEach(function (slide) {
      if (toHideSet.has(slide.dataset.id)) triggerVideoPause(slide);
    });

    allSlides.forEach(function (slide) {
      if (toHideSet.has(slide.dataset.id)) {
        slide.removeAttribute('data-product-single-media-wrapper');
        poolEl.appendChild(slide);
      }
    });
    allThumbs.forEach(function (thumb) {
      if (toHideSet.has(thumb.dataset.id)) poolEl.appendChild(thumb);
    });

    var sortedVisible = sortByVideoFirst(toShow);

    sortedVisible.forEach(function (slide) {
      slide.setAttribute('data-product-single-media-wrapper', '');
      slider.appendChild(slide);
    });

    if (thumbContainer) {
      var thumbByMediaId = {};
      allThumbs.forEach(function (t) { thumbByMediaId[t.dataset.id] = t; });
      sortedVisible.forEach(function (slide) {
        var thumb = thumbByMediaId[slide.dataset.id];
        if (thumb) thumbContainer.appendChild(thumb);
      });
    }

    if (typeof productInfo.checkSlider === 'function') {
      productInfo.checkSlider();
    }

    if (sortedVisible.length > 0) {
      setTimeout(function () { triggerVideoAutoplay(sortedVisible[0]); }, 100);
    }
  }

  // ── Dispatcher principal ───────────────────────────────────────────────────

  function applyFilter(productInfo, colorValue) {
    var tallLayout = productInfo.getAttribute('data-tall-layout') === 'true';
    var isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    var useFlickity = !tallLayout || isMobile;
    if (useFlickity) {
      applyFlickityFilter(productInfo, colorValue);
    } else {
      applyDesktopFilter(productInfo, colorValue);
    }
  }

  // ── Init ───────────────────────────────────────────────────────────────────

  function init() {
    pool = null;
    var productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    var slider = productInfo.querySelector('[data-product-single-media-slider]');
    var thumbContainer = productInfo.querySelector('[data-product-single-media-thumbs]');
    if (!slider) return;

    var slides = Array.from(
      slider.querySelectorAll('[data-product-single-media-wrapper][data-media-alt]')
    );
    var hasAnyAlt = slides.some(function (s) {
      return s.dataset.mediaAlt && s.dataset.mediaAlt.trim() !== '';
    });
    if (!hasAnyAlt) return;

    slides.forEach(function (slide, i) {
      slide.dataset.navirIndex = i;
      slide.setAttribute('data-navir-slide', '');
    });

    if (thumbContainer) {
      Array.from(
        thumbContainer.querySelectorAll('.product-single__thumbnail')
      ).forEach(function (thumb, i) {
        thumb.dataset.navirIndex = i;
        thumb.setAttribute('data-navir-thumb', '');
      });
    }

    var colorOptionIndex = findColorOptionIndex(productInfo);
    if (colorOptionIndex < 0) return;

    var initialColor = getSelectedColor(productInfo, colorOptionIndex);
    applyFilter(productInfo, initialColor);

    if (typeof subscribe === 'function' && window.theme && window.theme.PUB_SUB_EVENTS) {
      subscribe(window.theme.PUB_SUB_EVENTS.variantChange, function (event) {
        if (!event || !event.data || !event.data.variant) return;
        var color = event.data.variant.options
          ? event.data.variant.options[colorOptionIndex]
          : null;
        applyFilter(productInfo, color || null);
      });
    }

    var lastIsMobile = window.innerWidth < MOBILE_BREAKPOINT;
    document.addEventListener('theme:resize:width', function () {
      var nowMobile = window.innerWidth < MOBILE_BREAKPOINT;
      if (nowMobile !== lastIsMobile) {
        lastIsMobile = nowMobile;
        setTimeout(function () {
          var color = getSelectedColor(productInfo, colorOptionIndex);
          applyFilter(productInfo, color || null);
        }, 200);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', init);

}());
